import { useSyncExternalStore } from 'react';
import { isNative } from '@/lib/platform';
import { commands } from '@/lib/platform/generated/bindings.gen';
import { type ResolvedTheme } from '@/lib/platform/resolve-shell-theme';
import { useThemeSync } from './use-theme-sync';
import { pushWindowTheme } from './push-window-theme';

export type Theme = 'light' | 'dark' | 'system';

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: ResolvedTheme) {
  if (resolved === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

function resolve(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}

function safeParse(json: string): Record<string, unknown> {
  try {
    const obj = JSON.parse(json);
    return typeof obj === 'object' && obj !== null ? obj : {};
  } catch {
    return {};
  }
}

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem('nula_theme');
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // localStorage unavailable — fall through to system.
  }
  return 'system';
}

/* ──────────────────────────────────────────────────────────────────────────
 * Shared theme store
 *
 * useTheme is backed by a single module-level store, NOT per-component
 * useState. Every caller (RootLayout, the AppearanceTab, the share banner)
 * reads and writes the SAME state. Previously each call had its own useState +
 * its own DOM/persist effect, so multiple mounted instances fought: a stale
 * instance would re-apply its own value and clobber the user's selection
 * (e.g. "picking light snaps back to system"). One store = one writer = no
 * fight. Side effects (DOM, persistence, native window tint, OS listener) run
 * once here, driven by setTheme, not by N component effects.
 * ────────────────────────────────────────────────────────────────────────── */

// Read lazily on first subscribe (initStore), NOT at module load — localStorage
// may be written between import and first use (and tests rely on this ordering).
let currentTheme: Theme = 'system';
const listeners = new Set<() => void>();
let initialized = false;

function emit() {
  for (const l of listeners) l();
}

/** Apply the resolved theme to the DOM and tint the native window. */
function applyAndTint(theme: Theme) {
  applyTheme(resolve(theme));
  // Push the RAW theme to the native window: 'system' must clear the explicit
  // NSAppearance so the window (and thus prefers-color-scheme) follows the OS.
  void pushWindowTheme(theme);
}

/** Persist the theme (localStorage everywhere; settings.json + broadcast on desktop). */
function persist(theme: Theme) {
  try {
    localStorage.setItem('nula_theme', theme);
  } catch {
    // localStorage unavailable in this webview — Tauri settings still persist.
  }
  if (!isNative) return;
  // Route through commands.setTheme: it merges into settings.json AND emits a
  // theme-changed event to all windows so other open windows re-apply live.
  // commands.* returns a Result union — branch on status to handle errors explicitly.
  void commands.setTheme(theme).then((res) => {
    if (res.status === 'error') console.error(res.error);
  });
}

function setThemeStore(theme: Theme) {
  if (theme === currentTheme) {
    // Even if unchanged, re-apply (e.g. re-selecting system after an OS flip).
    applyAndTint(theme);
    return;
  }
  currentTheme = theme;
  applyAndTint(theme);
  persist(theme);
  emit();
}

/**
 * One-time store init: apply the synchronously-read theme, load the
 * authoritative desktop setting, and register the OS color-scheme listener.
 * Runs on first subscribe so it's a no-op until useTheme is actually used.
 */
function initStore() {
  if (initialized) return;
  initialized = true;

  // Read the stored preference now (localStorage is reliably available by first
  // subscribe) and apply immediately to prevent a flash.
  currentTheme = readStoredTheme();
  applyAndTint(currentTheme);

  // Desktop: load the authoritative settings.json value and correct if it
  // diverges from the localStorage mirror.
  if (isNative) {
    void commands.loadSettings().then((res) => {
      // On error, silently fall back to the localStorage mirror — non-fatal.
      if (res.status === 'error') return;
      const stored = safeParse(res.data).theme;
      if (
        (stored === 'light' || stored === 'dark' || stored === 'system') &&
        stored !== currentTheme
      ) {
        currentTheme = stored;
        applyAndTint(stored);
        emit();
      }
    });
  }

  // Re-resolve on OS color-scheme change while in system mode. One global
  // listener for the whole app (not one per component).
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (currentTheme === 'system') {
      applyTheme(getSystemTheme());
      // Keep the native window unpinned ('system') — pushing resolved light/dark
      // here would re-pin NSAppearance and break the next OS change.
      void pushWindowTheme('system');
    }
  });
}

function subscribe(cb: () => void): () => void {
  initStore();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): Theme {
  return currentTheme;
}

/**
 * Test-only: reset the module-level store so each test starts clean. The store
 * is global by design (single source of truth), which means it persists across
 * tests in a file — call this in beforeEach. Not for production use.
 */
export function resetThemeStoreForTests() {
  currentTheme = 'system';
  listeners.clear();
  initialized = false; // forces initStore to re-read localStorage on next subscribe
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Live cross-window theme sync for desktop (idempotent DOM writes; no-op web).
  useThemeSync();

  const setTheme = (t: Theme) => setThemeStore(t);

  // toggleTheme: cycles light ↔ dark (skips system), for callers that just want
  // a quick flip without knowing about the system option.
  const toggleTheme = () => setThemeStore(currentTheme === 'dark' ? 'light' : 'dark');

  return { theme, resolvedTheme: resolve(theme), setTheme, toggleTheme };
}
